import React, { useState, useEffect, useRef, useCallback } from 'react';
import { getSessions, createSession, connectSession, getQR, disconnectSession, logoutSession, deleteSession } from '../api/sessions';
import {
  HiOutlinePlus, HiOutlineXMark, HiOutlineSignal, HiOutlineArrowPath,
  HiOutlineTrash, HiOutlinePower, HiOutlineQrCode, HiOutlineDevicePhoneMobile,
  HiOutlineCheckCircle, HiOutlineExclamationTriangle, HiOutlineArrowRight
} from 'react-icons/hi2';
import toast from 'react-hot-toast';
import { useSocket } from '../context/SocketContext';

export default function WaSessions() {
  const [sessions, setSessions] = useState([]);
  const [loading, setLoading] = useState(true);
  // Wizard state: null | 'name' | 'connecting' | 'qr' | 'success'
  const [wizard, setWizard] = useState(null);
  const [wizardSessionId, setWizardSessionId] = useState(null);
  const [name, setName] = useState('');
  const [creating, setCreating] = useState(false);
  // Standalone QR modal (for reconnecting existing sessions)
  const [qrModal, setQrModal] = useState(null);
  const [qrData, setQrData] = useState(null);
  // Confirm dialog
  const [confirm, setConfirm] = useState(null); // { action, id, title, message }

  const pollRef = useRef(null);
  const refreshRef = useRef(null);
  const { subscribe } = useSocket() || {};

  // Track wizard and qrModal in refs so socket callbacks see latest values
  const wizardRef = useRef(wizard);
  wizardRef.current = wizard;
  const wizardSessionIdRef = useRef(wizardSessionId);
  wizardSessionIdRef.current = wizardSessionId;
  const qrModalRef = useRef(qrModal);
  qrModalRef.current = qrModal;

  const load = useCallback(async () => {
    try {
      const res = await getSessions();
      setSessions(res.data.data.sessions);
    } catch { } finally { setLoading(false); }
  }, []);

  // Initial load + 30-second fallback poll (instead of 5s)
  useEffect(() => {
    load();
    refreshRef.current = setInterval(load, 30000);
    return () => clearInterval(refreshRef.current);
  }, [load]);

  // Socket-driven real-time session updates
  useEffect(() => {
    if (!subscribe) return;

    // QR code received — update QR image instantly
    const unsubQR = subscribe('session:qr', (data) => {
      const activeId = wizardRef.current === 'qr' ? wizardSessionIdRef.current : qrModalRef.current;
      if (data.session_id === activeId && data.qr_data_url) {
        setQrData(data.qr_data_url);
      }
    });

    // Session connected — close QR, show success
    const unsubConnected = subscribe('session:connected', (data) => {
      load(); // refresh list

      const wizardActive = wizardRef.current === 'qr' && wizardSessionIdRef.current === data.session_id;
      const modalActive = qrModalRef.current === data.session_id;

      if (wizardActive) {
        setWizard('success');
        setQrData(null);
        if (pollRef.current) clearInterval(pollRef.current);
      } else if (modalActive) {
        toast.success('WhatsApp connected!');
        setQrModal(null);
        setQrData(null);
        if (pollRef.current) clearInterval(pollRef.current);
      }
    });

    // Session status changes (disconnected, error, reconnecting)
    const unsubStatus = subscribe('session:status', () => {
      load(); // refresh list to show updated status
    });

    return () => { unsubQR(); unsubConnected(); unsubStatus(); };
  }, [subscribe, load]);

  // QR fallback polling — only if socket isn't available or as safety net
  const activeQrId = wizard === 'qr' ? wizardSessionId : qrModal;

  useEffect(() => {
    if (!activeQrId) return;
    // Fetch QR once immediately (in case socket event was missed)
    const fetchQR = async () => {
      try {
        const res = await getQR(activeQrId);
        if (res.data.data.qr_data_url) {
          setQrData(res.data.data.qr_data_url);
        } else if (res.data.data.status === 'connected') {
          clearInterval(pollRef.current);
          if (wizard === 'qr') {
            setWizard('success');
          } else {
            toast.success('WhatsApp connected!');
            setQrModal(null);
            setQrData(null);
          }
          load();
        }
      } catch { }
    };
    fetchQR();
    // Fallback poll at 10s instead of 3s — socket handles the fast path
    pollRef.current = setInterval(fetchQR, 10000);
    return () => clearInterval(pollRef.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeQrId]);

  // === Wizard: Create & Connect in one flow ===
  const openWizard = () => {
    setWizard('name');
    setName('');
    setQrData(null);
    setWizardSessionId(null);
  };

  const closeWizard = () => {
    setWizard(null);
    setWizardSessionId(null);
    setName('');
    setQrData(null);
    if (pollRef.current) clearInterval(pollRef.current);
  };

  const handleCreateAndConnect = async (e) => {
    e.preventDefault();
    setCreating(true);
    try {
      const res = await createSession({ session_name: name });
      const sessionId = res.data.data?.session?.id;
      if (!sessionId) throw new Error('Failed to get session ID');
      setWizardSessionId(sessionId);
      setWizard('connecting');
      await connectSession(sessionId);
      setWizard('qr');
      load();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to create session');
      setWizard('name');
    } finally { setCreating(false); }
  };

  // === Existing session actions ===
  const handleConnect = async (id) => {
    try {
      await connectSession(id);
      setQrData(null);
      setQrModal(id);
      load();
    } catch (err) { toast.error(err.response?.data?.message || 'Failed'); }
  };

  const handleDisconnect = async (id) => {
    try { await disconnectSession(id); toast.success('Session disconnected'); load(); }
    catch (err) { toast.error(err.response?.data?.message || 'Failed'); }
  };

  const handleLogout = async (id) => {
    try { await logoutSession(id); toast.success('Device unlinked'); load(); setConfirm(null); }
    catch (err) { toast.error(err.response?.data?.message || 'Failed'); }
  };

  const handleDelete = async (id) => {
    try { await deleteSession(id); toast.success('Session deleted'); load(); setConfirm(null); }
    catch (err) { toast.error(err.response?.data?.message || 'Failed'); }
  };

  const getStatus = (s) => {
    const status = s.live_status || s.session_status;
    const map = {
      connected:    { color: 'text-green-600', bg: 'bg-green-50 border-green-200', dot: 'bg-green-500', label: 'Connected', pulse: true },
      connecting:   { color: 'text-yellow-600', bg: 'bg-yellow-50 border-yellow-200', dot: 'bg-yellow-500', label: 'Connecting', pulse: true },
      qr_required:  { color: 'text-blue-600', bg: 'bg-blue-50 border-blue-200', dot: 'bg-blue-500', label: 'Scan QR', pulse: true },
      disconnected: { color: 'text-gray-500', bg: 'bg-gray-50 border-gray-200', dot: 'bg-gray-400', label: 'Disconnected', pulse: false },
      error:        { color: 'text-red-600', bg: 'bg-red-50 border-red-200', dot: 'bg-red-500', label: 'Error', pulse: false },
      offline:      { color: 'text-gray-500', bg: 'bg-gray-50 border-gray-200', dot: 'bg-gray-400', label: 'Offline', pulse: false },
      new:          { color: 'text-gray-500', bg: 'bg-gray-50 border-gray-200', dot: 'bg-gray-400', label: 'Not Connected', pulse: false },
    };
    return map[status] || { color: 'text-gray-500', bg: 'bg-gray-50 border-gray-200', dot: 'bg-gray-400', label: status || 'Unknown', pulse: false };
  };

  const isConnectable = (s) => {
    const st = s.live_status || s.session_status;
    return ['offline', 'new', 'disconnected', 'error'].includes(st) || !st;
  };

  const needsQR = (s) => {
    const st = s.live_status || s.session_status;
    return st === 'qr_required';
  };

  const isConnected = (s) => (s.live_status || s.session_status) === 'connected';

  // Stepper dots
  const WizardStep = ({ step, current }) => {
    const steps = ['name', 'connecting', 'qr', 'success'];
    const idx = steps.indexOf(step);
    const curIdx = steps.indexOf(current);
    const done = curIdx > idx;
    const active = step === current;
    return (
      <div className={`w-2.5 h-2.5 rounded-full transition-all ${done ? 'bg-primary-500' : active ? 'bg-primary-500 ring-4 ring-primary-100' : 'bg-dark-200'}`} />
    );
  };

  return (
    <div>
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-6">
        <div>
          <h1 className="text-xl font-bold text-dark-800">WhatsApp Sessions</h1>
          <p className="text-sm text-dark-400 mt-0.5">
            {sessions.filter(s => isConnected(s)).length} of {sessions.length} connected
          </p>
        </div>
        <button onClick={openWizard} className="flex items-center gap-2 px-5 py-2.5 bg-primary-500 text-white rounded-xl text-sm font-semibold hover:bg-primary-600 transition-all shadow-sm hover:shadow-md">
          <HiOutlinePlus className="w-4 h-4" /> Add WhatsApp
        </button>
      </div>

      {/* Empty state */}
      {!loading && sessions.length === 0 && (
        <div className="bg-white rounded-2xl border border-dark-200 p-8 sm:p-12 text-center max-w-lg mx-auto">
          <div className="w-20 h-20 mx-auto mb-5 rounded-2xl bg-primary-50 flex items-center justify-center">
            <HiOutlineDevicePhoneMobile className="w-10 h-10 text-primary-500" />
          </div>
          <h2 className="text-lg font-bold text-dark-800 mb-2">Link your WhatsApp</h2>
          <p className="text-sm text-dark-400 mb-6 max-w-sm mx-auto">
            Connect your WhatsApp account to start sending and receiving messages. It only takes 30 seconds.
          </p>
          <button onClick={openWizard} className="inline-flex items-center gap-2 px-6 py-3 bg-primary-500 text-white rounded-xl text-sm font-semibold hover:bg-primary-600 transition-all shadow-sm">
            <HiOutlineQrCode className="w-5 h-5" /> Connect WhatsApp
          </button>
          <div className="mt-8 grid grid-cols-3 gap-3 text-center">
            <div className="p-3">
              <div className="w-8 h-8 mx-auto mb-2 rounded-lg bg-blue-50 flex items-center justify-center">
                <span className="text-sm font-bold text-blue-500">1</span>
              </div>
              <p className="text-xs text-dark-500">Click "Connect WhatsApp"</p>
            </div>
            <div className="p-3">
              <div className="w-8 h-8 mx-auto mb-2 rounded-lg bg-blue-50 flex items-center justify-center">
                <span className="text-sm font-bold text-blue-500">2</span>
              </div>
              <p className="text-xs text-dark-500">Scan QR with your phone</p>
            </div>
            <div className="p-3">
              <div className="w-8 h-8 mx-auto mb-2 rounded-lg bg-blue-50 flex items-center justify-center">
                <span className="text-sm font-bold text-blue-500">3</span>
              </div>
              <p className="text-xs text-dark-500">Start messaging!</p>
            </div>
          </div>
        </div>
      )}

      {/* Loading */}
      {loading && (
        <div className="flex items-center justify-center py-16">
          <div className="w-8 h-8 border-3 border-primary-500 border-t-transparent rounded-full animate-spin" />
        </div>
      )}

      {/* Session cards */}
      {!loading && sessions.length > 0 && (
        <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-3">
          {sessions.map((s) => {
            const st = getStatus(s);
            return (
              <div key={s.id} className={`bg-white rounded-xl border ${isConnected(s) ? 'border-green-200' : 'border-dark-200'} overflow-hidden hover:shadow-md transition-all`}>
                {/* Card header with status stripe */}
                <div className={`h-1 ${isConnected(s) ? 'bg-green-500' : needsQR(s) ? 'bg-blue-500' : 'bg-dark-200'}`} />
                <div className="p-5">
                  <div className="flex justify-between items-start mb-4">
                    <div className="flex items-center gap-3">
                      <div className={`w-11 h-11 rounded-xl flex items-center justify-center ${isConnected(s) ? 'bg-green-50' : 'bg-gray-50'}`}>
                        <HiOutlineSignal className={`w-5 h-5 ${isConnected(s) ? 'text-green-600' : 'text-dark-400'}`} />
                      </div>
                      <div>
                        <h3 className="font-semibold text-dark-800 text-sm">{s.session_name}</h3>
                        <p className="text-xs text-dark-400 mt-0.5">
                          {s.phone_number || 'Not linked'}
                          {s.display_name ? ` · ${s.display_name}` : ''}
                        </p>
                      </div>
                    </div>
                    {/* Status badge */}
                    <div className={`flex items-center gap-1.5 px-2.5 py-1 rounded-lg border text-xs font-medium ${st.bg} ${st.color}`}>
                      <span className={`w-1.5 h-1.5 rounded-full ${st.dot} ${st.pulse ? 'animate-pulse' : ''}`} />
                      {st.label}
                    </div>
                  </div>

                  {/* Action buttons */}
                  <div className="flex items-center gap-2 pt-3 border-t border-dark-100">
                    {isConnectable(s) && (
                      <button onClick={() => handleConnect(s.id)}
                        className="flex-1 flex items-center justify-center gap-1.5 px-3 py-2 bg-primary-500 text-white rounded-lg text-xs font-semibold hover:bg-primary-600 transition-colors">
                        <HiOutlineQrCode className="w-4 h-4" /> Connect
                      </button>
                    )}
                    {needsQR(s) && (
                      <button onClick={() => { setQrData(null); setQrModal(s.id); }}
                        className="flex-1 flex items-center justify-center gap-1.5 px-3 py-2 bg-blue-500 text-white rounded-lg text-xs font-semibold hover:bg-blue-600 transition-colors animate-pulse">
                        <HiOutlineQrCode className="w-4 h-4" /> Scan QR Code
                      </button>
                    )}
                    {isConnected(s) && (
                      <button onClick={() => handleDisconnect(s.id)}
                        className="flex-1 flex items-center justify-center gap-1.5 px-3 py-2 border border-dark-200 text-dark-600 rounded-lg text-xs font-medium hover:bg-gray-50 transition-colors">
                        <HiOutlinePower className="w-3.5 h-3.5" /> Disconnect
                      </button>
                    )}
                    {s.phone_number && (
                      <button onClick={() => setConfirm({ action: 'logout', id: s.id, title: 'Unlink Device', message: `This will unlink "${s.session_name}" from WhatsApp. You'll need to scan the QR code again to reconnect.` })}
                        className="p-2 border border-orange-200 text-orange-500 rounded-lg text-xs hover:bg-orange-50 transition-colors" title="Unlink device">
                        <HiOutlineDevicePhoneMobile className="w-4 h-4" />
                      </button>
                    )}
                    <button onClick={() => setConfirm({ action: 'delete', id: s.id, title: 'Delete Session', message: `Permanently delete "${s.session_name}"? This cannot be undone.` })}
                      className="p-2 border border-red-200 text-red-400 rounded-lg text-xs hover:bg-red-50 transition-colors" title="Delete session">
                      <HiOutlineTrash className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* ====== Create & Connect Wizard Modal ====== */}
      {wizard && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={(e) => e.target === e.currentTarget && wizard !== 'connecting' && closeWizard()}>
          <div className="bg-white rounded-2xl w-full max-w-md shadow-2xl animate-fadeIn overflow-hidden">
            {/* Progress dots */}
            <div className="flex items-center justify-center gap-2 pt-5 pb-2">
              {['name', 'connecting', 'qr', 'success'].map((step) => (
                <WizardStep key={step} step={step} current={wizard} />
              ))}
            </div>

            {/* Step 1: Name */}
            {wizard === 'name' && (
              <div className="p-6">
                <div className="text-center mb-6">
                  <div className="w-14 h-14 mx-auto mb-3 rounded-2xl bg-primary-50 flex items-center justify-center">
                    <HiOutlinePlus className="w-7 h-7 text-primary-500" />
                  </div>
                  <h2 className="text-lg font-bold text-dark-800">Add WhatsApp Account</h2>
                  <p className="text-sm text-dark-400 mt-1">Give your session a name to identify it</p>
                </div>
                <form onSubmit={handleCreateAndConnect}>
                  <input
                    type="text" required autoFocus
                    placeholder="e.g. My Phone, Sales Team, Support"
                    value={name} onChange={(e) => setName(e.target.value)}
                    className="w-full px-4 py-3 border border-dark-200 rounded-xl text-sm outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500 mb-4"
                  />
                  <div className="flex gap-3">
                    <button type="button" onClick={closeWizard} className="flex-1 py-3 border border-dark-200 rounded-xl text-sm font-medium text-dark-600 hover:bg-gray-50 transition-colors">Cancel</button>
                    <button type="submit" disabled={!name.trim() || creating}
                      className="flex-1 flex items-center justify-center gap-2 py-3 bg-primary-500 text-white rounded-xl text-sm font-semibold hover:bg-primary-600 disabled:opacity-50 transition-colors">
                      Next <HiOutlineArrowRight className="w-4 h-4" />
                    </button>
                  </div>
                </form>
              </div>
            )}

            {/* Step 2: Connecting */}
            {wizard === 'connecting' && (
              <div className="p-8 text-center">
                <div className="w-16 h-16 mx-auto mb-4">
                  <div className="w-16 h-16 border-4 border-primary-500 border-t-transparent rounded-full animate-spin" />
                </div>
                <h2 className="text-lg font-bold text-dark-800 mb-1">Setting up session...</h2>
                <p className="text-sm text-dark-400">Creating "{name}" and generating QR code</p>
              </div>
            )}

            {/* Step 3: QR Code */}
            {wizard === 'qr' && (
              <div className="p-6 text-center">
                <h2 className="text-lg font-bold text-dark-800 mb-1">Scan QR Code</h2>
                <p className="text-sm text-dark-400 mb-5">Open WhatsApp on your phone to scan</p>

                {qrData ? (
                  <div className="inline-block p-4 bg-white rounded-2xl border-2 border-dark-100 shadow-sm mb-5">
                    <img src={qrData} alt="WhatsApp QR Code" className="w-52 h-52 sm:w-60 sm:h-60" />
                  </div>
                ) : (
                  <div className="py-12">
                    <div className="w-10 h-10 border-3 border-primary-500 border-t-transparent rounded-full animate-spin mx-auto mb-3" />
                    <p className="text-sm text-dark-400">Generating QR code...</p>
                  </div>
                )}

                <div className="bg-gray-50 rounded-xl p-4 mb-5 text-left">
                  <div className="flex items-start gap-3">
                    <HiOutlineDevicePhoneMobile className="w-5 h-5 text-primary-500 mt-0.5 flex-shrink-0" />
                    <div className="text-xs text-dark-500 space-y-1">
                      <p><strong className="text-dark-700">On your phone:</strong></p>
                      <p>WhatsApp → Settings → Linked Devices → Link a Device</p>
                    </div>
                  </div>
                </div>

                <div className="flex gap-3">
                  <button onClick={() => { setQrData(null); }}
                    className="flex-1 flex items-center justify-center gap-1.5 py-2.5 border border-dark-200 rounded-xl text-sm font-medium text-dark-600 hover:bg-gray-50 transition-colors">
                    <HiOutlineArrowPath className="w-4 h-4" /> Refresh
                  </button>
                  <button onClick={closeWizard}
                    className="flex-1 py-2.5 border border-dark-200 rounded-xl text-sm font-medium text-dark-600 hover:bg-gray-50 transition-colors">
                    Done
                  </button>
                </div>
                <p className="text-[11px] text-dark-300 mt-3">QR updates in real-time</p>
              </div>
            )}

            {/* Step 4: Success */}
            {wizard === 'success' && (
              <div className="p-8 text-center">
                <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-green-50 flex items-center justify-center">
                  <HiOutlineCheckCircle className="w-10 h-10 text-green-500" />
                </div>
                <h2 className="text-lg font-bold text-dark-800 mb-1">Connected!</h2>
                <p className="text-sm text-dark-400 mb-6">Your WhatsApp is linked and ready to go. You can now send and receive messages.</p>
                <div className="flex gap-3">
                  <button onClick={closeWizard} className="flex-1 py-3 border border-dark-200 rounded-xl text-sm font-medium text-dark-600 hover:bg-gray-50 transition-colors">Stay Here</button>
                  <a href="/conversations" className="flex-1 flex items-center justify-center gap-2 py-3 bg-primary-500 text-white rounded-xl text-sm font-semibold hover:bg-primary-600 transition-colors">
                    Start Chatting <HiOutlineArrowRight className="w-4 h-4" />
                  </a>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* ====== Standalone QR Modal (for reconnecting) ====== */}
      {qrModal && !wizard && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={(e) => e.target === e.currentTarget && setQrModal(null)}>
          <div className="bg-white rounded-2xl w-full max-w-sm shadow-2xl animate-fadeIn p-6 text-center">
            <div className="flex justify-between items-center mb-4">
              <h2 className="font-bold text-dark-800">Scan to Reconnect</h2>
              <button onClick={() => { setQrModal(null); setQrData(null); }} className="p-1 hover:bg-gray-100 rounded-lg">
                <HiOutlineXMark className="w-5 h-5 text-dark-400" />
              </button>
            </div>
            {qrData ? (
              <div className="inline-block p-4 bg-white rounded-2xl border-2 border-dark-100 shadow-sm mb-4">
                <img src={qrData} alt="QR Code" className="w-52 h-52 sm:w-60 sm:h-60" />
              </div>
            ) : (
              <div className="py-12">
                <div className="w-10 h-10 border-3 border-primary-500 border-t-transparent rounded-full animate-spin mx-auto mb-3" />
                <p className="text-sm text-dark-400">Generating QR code...</p>
              </div>
            )}
            <p className="text-xs text-dark-400 mb-4">WhatsApp → Linked Devices → Link a Device</p>
            <button onClick={() => { setQrModal(null); setQrData(null); }}
              className="w-full py-2.5 border border-dark-200 rounded-xl text-sm font-medium hover:bg-gray-50 transition-colors">Close</button>
          </div>
        </div>
      )}

      {/* ====== Confirm Dialog ====== */}
      {confirm && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={(e) => e.target === e.currentTarget && setConfirm(null)}>
          <div className="bg-white rounded-2xl w-full max-w-sm shadow-2xl animate-fadeIn p-6">
            <div className="flex items-center gap-3 mb-3">
              <div className="w-10 h-10 rounded-xl bg-red-50 flex items-center justify-center flex-shrink-0">
                <HiOutlineExclamationTriangle className="w-5 h-5 text-red-500" />
              </div>
              <h3 className="font-bold text-dark-800">{confirm.title}</h3>
            </div>
            <p className="text-sm text-dark-500 mb-6 ml-13">{confirm.message}</p>
            <div className="flex gap-3">
              <button onClick={() => setConfirm(null)} className="flex-1 py-2.5 border border-dark-200 rounded-xl text-sm font-medium text-dark-600 hover:bg-gray-50 transition-colors">Cancel</button>
              <button onClick={() => confirm.action === 'delete' ? handleDelete(confirm.id) : handleLogout(confirm.id)}
                className="flex-1 py-2.5 bg-red-500 text-white rounded-xl text-sm font-semibold hover:bg-red-600 transition-colors">
                {confirm.action === 'delete' ? 'Delete' : 'Unlink'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
